/**
 * HandoffLinkModal - Phase H7.
 *
 * Operator-facing modal that calls `POST /api/handoffs/whatsapp-link` and
 * surfaces the resulting `wa.me` URL. Designed to be opened from the Inbox
 * "Move to WhatsApp" button on Instagram / Messenger conversations.
 *
 * UX rules (per H7 spec):
 *   - No auto-send. The operator decides when to deliver the link.
 *   - No raw internal contact IDs in the wa.me URL - the handoff token
 *     marker is the only secret-shaped artifact, and that's required.
 *   - Display the masked token + expiry so the operator can sanity-check.
 *   - Optional "Insert into reply" callback so the parent (ConversationPane)
 *     can drop the link into the existing IG/Messenger ReplyBox for the
 *     operator to send to the lead on the original channel.
 *
 * The modal is dumb about target-phone resolution: the parent passes
 * `defaultTargetPhone` when known (e.g. from a future business setting);
 * otherwise the operator types it once per session. The worker also has
 * its own fallback (`env.WAHA_BUSINESS_NUMBER`).
 */

import { useEffect, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Loader2, Send, X } from "lucide-react";
import { HttpError } from "@/lib/api-client";
import { parseBackendDate } from "@/lib/format";
import {
  createWhatsappHandoffLink,
  type CreateWhatsappHandoffLinkInput,
  type HandoffLinkResult,
} from "@/lib/api-handoffs";

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Pre-filled inputs from the calling context (Inbox ConversationPane).
   * The operator can edit `targetPhone`, `message`, `campaignId`, `ref`
   * inside the modal; everything else is committed at open time.
   */
  initial: Omit<CreateWhatsappHandoffLinkInput, "targetPhone" | "message"> & {
    targetPhone?: string;
    message?: string;
    /** Cosmetic label for the operator: "Instagram DM", "Messenger". */
    sourceLabel?: string;
  };
  /**
   * Optional: when provided, a button appears that pushes
   * `"You can continue on WhatsApp here: <waUrl>"` into the parent's reply
   * box. The parent is responsible for actually sending.
   */
  onInsertIntoReply?: (text: string) => void;
}

const DEFAULT_MESSAGE = "Hi, I want to continue on WhatsApp.";

export function HandoffLinkModal({
  open,
  onClose,
  initial,
  onInsertIntoReply,
}: Props) {
  const [targetPhone, setTargetPhone] = useState("");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [campaignId, setCampaignId] = useState("");
  const [ref, setRef] = useState("");
  const [result, setResult] = useState<HandoffLinkResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset every time the modal is (re-)opened.
  useEffect(() => {
    if (open) {
      setTargetPhone(initial.targetPhone ?? "");
      setMessage(initial.message ?? DEFAULT_MESSAGE);
      setCampaignId(initial.campaignId ?? "");
      setRef(initial.ref ?? "");
      setResult(null);
      setCopied(false);
      setError(null);
    }
  }, [open, initial]);

  const createMut = useMutation({
    mutationFn: () =>
      createWhatsappHandoffLink({
        ...initial,
        targetPhone: targetPhone.trim() || undefined,
        message: message.trim() || undefined,
        campaignId: campaignId.trim() || undefined,
        ref: ref.trim() || undefined,
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof HttpError) {
        const body = err.body as { message?: string; error?: string } | null;
        setError(body?.message || body?.error || `HTTP ${err.status}`);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
  });

  if (!open) return null;

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    createMut.mutate();
  }

  async function copyLink() {
    if (!result?.waUrl) return;
    try {
      await navigator.clipboard.writeText(result.waUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable in some environments - fall back to
      // selecting the URL textarea for manual copy.
      setError(
        "Copy failed - select the link below manually and copy with Ctrl/Cmd+C.",
      );
    }
  }

  function insertIntoReply() {
    if (!result?.waUrl || !onInsertIntoReply) return;
    onInsertIntoReply(`You can continue on WhatsApp here: ${result.waUrl}`);
    onClose();
  }

  const isHandoff = result?.mode === "handoff";
  const handoffToken = isHandoff ? result.handoffToken : null;
  const expiresAt = isHandoff ? result.expiresAt : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-brand-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Move to WhatsApp
            </h2>
            <p className="text-xs text-slate-500">
              {initial.sourceLabel ? `From ${initial.sourceLabel}. ` : ""}
              This link lets the system continue the same conversation on WhatsApp.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={submit} className="space-y-3 px-5 py-4">
          <Field label="Business WhatsApp number">
            <Input
              type="tel"
              placeholder="+62 811 222 333"
              value={targetPhone}
              onChange={(e) => setTargetPhone(e.target.value)}
              disabled={createMut.isPending || result !== null}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Leave empty to use the worker's configured WAHA_BUSINESS_NUMBER.
            </p>
          </Field>

          <Field label="Prefilled message">
            <Input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={createMut.isPending || result !== null}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Campaign ID (optional)">
              <Input
                type="text"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                disabled={createMut.isPending || result !== null}
              />
            </Field>
            <Field label="Ref (optional)">
              <Input
                type="text"
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                disabled={createMut.isPending || result !== null}
              />
            </Field>
          </div>

          {error && (
            <div className="rounded bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          {result ? (
            <div className="space-y-2 rounded border border-slate-200 bg-slate-50 px-3 py-3">
              <div className="text-xs font-medium text-slate-700">
                Generated wa.me link ({result.mode})
              </div>
              <textarea
                readOnly
                value={result.waUrl}
                rows={3}
                className="w-full resize-none rounded border border-slate-300 bg-brand-surface px-2 py-1.5 text-xs font-mono text-slate-800"
                onFocus={(e) => e.currentTarget.select()}
              />
              {handoffToken && (
                <div className="text-[11px] text-slate-500">
                  Token:{" "}
                  <code className="rounded bg-white px-1 py-0.5 font-mono">
                    {maskToken(handoffToken)}
                  </code>
                  {expiresAt && (
                    <>
                      {" · "}
                      Expires {parseBackendDate(expiresAt).toLocaleString()}
                    </>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={copyLink}
                >
                  <Copy className="h-4 w-4" />
                  {copied ? "Copied!" : "Copy link"}
                </Button>
                {onInsertIntoReply && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={insertIntoReply}
                  >
                    <Send className="h-4 w-4" />
                    Insert into reply
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setResult(null)}
                >
                  Generate another
                </Button>
              </div>
              <p className="pt-1 text-[11px] text-slate-500">
                The handoff link is NOT auto-sent. Paste it into the chat
                with the lead manually.
              </p>
            </div>
          ) : (
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onClose}
                disabled={createMut.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                variant="primary"
                disabled={createMut.isPending}
              >
                {createMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Generate link
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function maskToken(token: string): string {
  if (token.length <= 10) return "hf_***";
  const body = token.startsWith("hf_") ? token.slice(3) : token;
  return `hf_${body.slice(0, 4)}...${body.slice(-4)}`;
}
